import { Button } from '@/components/ui/button';
import { Search, MessageCircle, Phone, ChevronLeft, ChevronRight, Car, Video, CreditCard, Shield, ShoppingCart, FileText, Wrench, Users, Package, Settings, Gauge, Handshake, HandCoins, TrendingUp, Calendar, Fuel, Palette, Bike, Truck, Building2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import { useAppData } from '@/contexts/AppDataContext';
import { createPortal } from 'react-dom';
import { openWhatsApp } from '@/lib/whatsapp';
import { generateVehicleSlug } from '@/lib/slug';
import { ServiceModal } from '@/components/ServiceModal';
import { useStore } from '@/contexts/StoreContext';
import { useIsMobile } from '@/hooks/use-mobile';

// Import service images
import videoCall from '@/assets/services/videoCall.png';
import valuation from '@/assets/services/valuation.png';
import consignment from '@/assets/services/consignment.png';
import consortium from '@/assets/services/consortium.png';
import forwardingAgent from '@/assets/services/forwardingAgent.png';
import financing from '@/assets/services/financing.png';
import partsAccessories from '@/assets/services/partsAccessories.png';
import scheduleReview from '@/assets/services/scheduleReview.png';
import insurance from '@/assets/services/insurance.png';
import scheduleServices from '@/assets/services/scheduleServices.png';
import fipeTable from '@/assets/services/fipeTable.png';
import testdrive from '@/assets/services/testdrive.png';
import directSales from '@/assets/services/directSales.png';
import { getBrandLogo, getBrandVehicleType } from '@/lib/brandLogos';

interface HeroProps {
  config: any;
}

export const Hero = ({ config }: HeroProps) => {
  const { selectedStore, availableStores } = useStore();
  const store = selectedStore || config.stores[0];
  const isMobile = useIsMobile();
  const banners = store.banners?.filter(v => v.platform === (isMobile ? 'mobile' : 'web')) || [];
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  const [nextBannerIndex, setNextBannerIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const [showBrandNavigation, setShowBrandNavigation] = useState(false);
  const [showFloatingSearch, setShowFloatingSearch] = useState(false);
  const [isFloatingSearchVisible, setIsFloatingSearchVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [brandsLoading, setBrandsLoading] = useState(true);
  const [animatedPlaceholder, setAnimatedPlaceholder] = useState('Pesquisar por ...');
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const floatingInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const brandsScrollRef = useRef<HTMLDivElement>(null);
  const searchCardRef = useRef<HTMLDivElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { vehicles, allVehicles } = useAppData();

  // Filter vehicles based on unifiedAds and unifiedAdsStore rules
  const filteredVehicles = useMemo(() => {
    const unifiedAds = config?.unifiedAds !== false;
    const unifiedAdsStore = config?.unifiedAdsStore;
    const selectedStoreId = selectedStore?._id;

    // Rule 1: If unifiedAds is false, always filter by selected store
    if (!unifiedAds) {
      if (selectedStoreId) {
        return allVehicles.filter(v => v.store === selectedStoreId);
      }
      return allVehicles;
    }

    // Rule 2: If unifiedAds is true and unifiedAdsStore is null, show all vehicles
    if (unifiedAdsStore === null || unifiedAdsStore === undefined) {
      return allVehicles;
    }

    // Rule 3 & 4: If unifiedAds is true and unifiedAdsStore is set
    if (selectedStoreId === unifiedAdsStore) {
      // Rule 3: Selected store is the unified store → show all vehicles
      return allVehicles;
    } else {
      // Rule 4: Selected store is different → filter by selected store
      if (selectedStoreId) {
        return allVehicles.filter(v => v.store === selectedStoreId);
      }
      return allVehicles;
    }
  }, [config?.unifiedAds, config?.unifiedAdsStore, allVehicles, selectedStore?._id]);

  const buttonColor = colors.button;
  const primaryColor = colors.primary;
  const secondaryColor = colors.secondary;
  const titleColor = colors.title;
  const textColor = colors.text;

  // Get main service info
  const mainServiceId = store.mainService;
  const mainService = store.services?.find((s: any) => s.service === mainServiceId);

  const serviceInfo: Record<string, { icon: any; title: string; description: string; image: string; color: string }> = {
    '65da42048eca108d87c91da6': { icon: Video, title: 'Agendar Videochamada', description: 'Agende uma videochamada para conhecer nossos veículos sem sair de casa', image: videoCall, color: 'bg-blue-500' },
    '65dcb5e78eca108d87c91db8': { icon: Gauge, title: 'Test-Drive', description: 'Agende um test drive e experimente o veículo dos seus sonhos', image: testdrive, color: 'bg-indigo-500' },
    '65dcb7548eca108d87c91dbb': { icon: Search, title: 'Avaliação', description: 'Avaliação profissional do seu veículo com preço justo de mercado', image: valuation, color: 'bg-green-500' },
    '65dcb7898eca108d87c91dbc': { icon: CreditCard, title: 'Financiamento', description: 'Análise de crédito e financiamento com as melhores taxas do mercado', image: financing, color: 'bg-blue-600' },
    '65dcb7c58eca108d87c91dbd': { icon: HandCoins, title: 'Consignação', description: 'Deixe seu veículo em consignação conosco e venda com segurança', image: consignment, color: 'bg-purple-500' },
    '65dcef468eca108d87c91dc8': { icon: Users, title: 'Consórcio', description: 'Adquira seu veículo através de consórcio com as melhores condições', image: consortium, color: 'bg-teal-500' },
    '65dcb6528eca108d87c91db9': { icon: FileText, title: 'Despachante', description: 'Serviços de despachante para documentação veicular completa', image: forwardingAgent, color: 'bg-orange-500' },
    '65dcb86a8eca108d87c91dc0': { icon: Shield, title: 'Seguros', description: 'Cotação de seguros com as principais seguradoras do país', image: insurance, color: 'bg-red-500' },
    '6628fd535bda2ae51f6fc5ff': { icon: Package, title: 'Peças e Acessórios', description: 'Peças originais e acessórios para seu veículo', image: partsAccessories, color: 'bg-gray-600' },
    '65dceeac8eca108d87c91dc6': { icon: Settings, title: 'Serviços', description: 'Serviços automotivos especializados para seu veículo', image: scheduleServices, color: 'bg-indigo-600' },
    '65dcef098eca108d87c91dc7': { icon: Wrench, title: 'Revisões', description: 'Revisões preventivas e corretivas com equipe especializada', image: scheduleReview, color: 'bg-slate-600' },
    '65dcef498eca108d87c91dc8': { icon: TrendingUp, title: 'Tabela FIPE', description: 'Consulte o valor do seu veículo pela tabela FIPE', image: fipeTable, color: 'bg-cyan-500' },
    '65dcb8548eca108d87c91dbf': { icon: Handshake, title: 'Vendas Diretas', description: 'Vendas diretas com as melhores condições e atendimento personalizado', image: directSales, color: 'bg-green-600' }
  };

  const mainServiceInfo = mainServiceId ? serviceInfo[mainServiceId] : null;
  const MainServiceIcon = mainServiceInfo?.icon || MessageCircle;

  // Generate dynamic placeholder options from available vehicles
  const placeholderOptions = useMemo(() => {
    const options: { type: string; value: string }[] = [];

    // Get unique brands (top 2)
    const brands = Array.from(new Set(filteredVehicles.map(v => v.brand))).slice(0, 2);
    brands.forEach(brand => options.push({ type: 'marca', value: `${brand}...` }));

    // Get popular models (top 2)
    const models = Array.from(new Set(filteredVehicles.map(v => v.model))).slice(0, 2);
    models.forEach(model => options.push({ type: 'modelo', value: `${model}...` }));

    // Get some versions (top 2)
    const versions = Array.from(new Set(
      filteredVehicles
        .filter(v => v.version)
        .map(v => v.version)
    )).slice(0, 2);
    versions.forEach(version => options.push({ type: 'versão', value: `${version}...` }));

    // Get colors (top 2)
    const colors = Array.from(new Set(
      filteredVehicles
        .filter(v => v.color)
        .map(v => v.color)
    )).slice(0, 2);
    colors.forEach(color => options.push({ type: 'cor', value: `${color}...` }));

    // Get transmissions (top 2)
    const transmissions = Array.from(new Set(
      filteredVehicles
        .filter(v => v.transmission)
        .map(v => v.transmission)
    )).slice(0, 2);
    transmissions.forEach(transmission => options.push({ type: 'câmbio', value: `${transmission}...` }));

    // Get fuel types (top 2)
    const fuels = Array.from(new Set(
      filteredVehicles
        .filter(v => v.fuel)
        .map(v => v.fuel)
    )).slice(0, 2);
    fuels.forEach(fuel => options.push({ type: 'combustível', value: `${fuel}...` }));

    // Get years (top 2)
    const years = Array.from(new Set(
      filteredVehicles.map(v => v.modelYear)
    )).sort((a, b) => b - a).slice(0, 2);
    years.forEach(year => options.push({ type: 'ano', value: `${year}...` }));

    return options.length > 0 ? options : [{ type: 'marca, modelo ou versão', value: '' }];
  }, [filteredVehicles]);

  // Animated placeholder with typing effect
  useEffect(() => {
    if (searchTerm) {
      setAnimatedPlaceholder('Pesquisar por ...');
      return; // Don't animate if user is typing
    }

    let currentOptionIndex = 0;
    let charIndex = 15; // Start after "Pesquisar por " (15 chars)
    let isDeleting = false;
    let isTypingType = true; // Track if we're typing the type part
    let timeoutId: NodeJS.Timeout;

    const baseText = 'Pesquisar por ';
    
    const animate = () => {
      const currentOption = placeholderOptions[currentOptionIndex];
      const typeText = baseText + currentOption.type + ' ';
      const fullText = baseText + currentOption.type + ' ' + currentOption.value;

      if (isTypingType) {
        // Typing the type part: "Pesquisar por marca "
        if (charIndex <= typeText.length) {
          setAnimatedPlaceholder(typeText.substring(0, charIndex));
          charIndex++;
          timeoutId = setTimeout(animate, 50); // Typing speed
        } else {
          // Start typing the value
          isTypingType = false;
          animate();
        }
      } else if (!isDeleting) {
        // Typing the value part: "Chevrolet..."
        if (charIndex <= fullText.length) {
          setAnimatedPlaceholder(fullText.substring(0, charIndex));
          charIndex++;
          timeoutId = setTimeout(animate, 50); // Typing speed
        } else {
          // Wait before deleting
          timeoutId = setTimeout(() => {
            isDeleting = true;
            animate();
          }, 2000); // Wait time at end
        }
      } else {
        // Deleting - only delete the value part, back to "Pesquisar por "
        if (charIndex > baseText.length) {
          charIndex--;
          setAnimatedPlaceholder(fullText.substring(0, charIndex));
          timeoutId = setTimeout(animate, 20); // Deleting speed (faster)
        } else {
          // Move to next option - start from baseText length
          isDeleting = false;
          isTypingType = true;
          currentOptionIndex = (currentOptionIndex + 1) % placeholderOptions.length;
          charIndex = baseText.length; // Reset to 15 to start after "Pesquisar por "
          timeoutId = setTimeout(animate, 200); // Pause before next type
        }
      }
    };

    // Start animation immediately
    timeoutId = setTimeout(animate, 100);

    return () => clearTimeout(timeoutId);
  }, [placeholderOptions, searchTerm]);
  const mainServiceTitle = mainServiceInfo?.title || 'Fale Conosco';
  const mainServiceImage = mainService?.image || mainServiceInfo?.image;

  const getServiceButtonText = (serviceId: string) => {
    const buttonTexts: Record<string, string> = {
      // Agendar
      '65da42048eca108d87c91da6': 'Agendar', // Agendar Videochamada
      '65dceeac8eca108d87c91dc6': 'Agendar', // Serviços
      '65dcef098eca108d87c91dc7': 'Agendar', // Revisões
      '65dcb5e78eca108d87c91db8': 'Agendar', // Test-Drive

      // Simular
      '65dcb7898eca108d87c91dbc': 'Simular', // Financiamento

      // Avaliar
      '65dcb7548eca108d87c91dbb': 'Avaliar', // Avaliação
      '65dcb7c58eca108d87c91dbd': 'Avaliar', // Consignação

      // Cotar
      '65dcb86a8eca108d87c91dc0': 'Cotar', // Seguros
      '65dcb6528eca108d87c91db9': 'Cotar', // Despachante
      '6628fd535bda2ae51f6fc5ff': 'Cotar', // Peças e Acessórios

      // Solicitar
      '65dcef468eca108d87c91dc8': 'Solicitar', // Consórcio
      '65dcb8548eca108d87c91dbf': 'Solicitar', // Vendas Diretas

      // Consultar
      '65dcef498eca108d87c91dc8': 'Consultar', // Tabela FIPE
    };

    return buttonTexts[serviceId] || 'Solicitar Serviço';
  };

  // Get unique brands (from filtered vehicles)
  const brands = useMemo(() =>
    Array.from(new Set(filteredVehicles.map(v => v.brand))).sort(),
    [filteredVehicles]
  );

  // Update brands loading state
  useEffect(() => {
    if (brands.length > 0) {
      // Small delay to simulate loading and avoid flash
      const timer = setTimeout(() => {
        setBrandsLoading(false);
      }, 100);
      return () => clearTimeout(timer);
    } else {
      setBrandsLoading(true);
    }
  }, [brands]);

  // Generate autocomplete suggestions
  const suggestions = useMemo(() => {
    if (searchTerm.length < 2) return [];

    const term = searchTerm.toLowerCase();
    const results: Array<{ type: string; text: string; vehicle?: any; filterKey?: string; filterValue?: string; count?: number; minYear?: number; maxYear?: number; storeId?: string }> = [];


    // Suggest stores (show ALL stores from config, including hidden ones)
    // Only show if there are multiple stores
    const allStores = config?.stores || [];
    if (allStores.length > 1) {
      allStores.forEach((store: any) => {
        if (store.company.toLowerCase().includes(term)) {
          // Use allVehicles to count all vehicles from this store
          const count = allVehicles.filter(v => v.store === store._id).length;
          if (count > 0) {
            results.push({
              type: 'store',
              text: store.company,
              filterKey: 'store',
              filterValue: store._id,
              storeId: store._id,
              count
            });
          }
        }
      });
    }

    // Suggest vehicle types (Carro, Moto, Caminhão)
    const vehicleTypes = [
      { text: 'Carro', value: '1' },
      { text: 'Moto', value: '2' },
      { text: 'Caminhão', value: '3' }
    ];
    vehicleTypes.forEach(({ text, value }) => {
      if (text.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.type.toString() === value).length;
        if (count > 0) {
          results.push({ type: 'category', text: text, filterKey: 'category', filterValue: value, count });
        }
      }
    });

    // Suggest brands
    brands.forEach(brand => {
      if (brand.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.brand === brand).length;
        results.push({ type: 'brand', text: brand, count });
      }
    });

    // Suggest models
    const models = Array.from(new Set(filteredVehicles.map(v => `${v.brand} ${v.model}`)));
    models.forEach(model => {
      if (model.toLowerCase().includes(term) && !results.find(s => s.text === model)) {
        const count = filteredVehicles.filter(v => `${v.brand} ${v.model}` === model).length;
        results.push({ type: 'model', text: model, count });
      }
    });

    // Suggest years (count exact, navigate with same year for both min and max)
    const years = Array.from(new Set(filteredVehicles.map(v => v.modelYear))).sort((a, b) => b - a);
    years.forEach(year => {
      if (year.toString().includes(term)) {
        // Count only vehicles with exact modelYear
        const count = filteredVehicles.filter(v => v.modelYear === year).length;

        if (count > 0) {
          // Set both minYear and maxYear to the same year
          const minYear = year;
          const maxYear = year;

          results.push({
            type: 'year',
            text: year.toString(),
            filterKey: 'yearRange',
            filterValue: `${minYear}-${maxYear}`,
            minYear,
            maxYear,
            count
          });
        }
      }
    });

    // Suggest fuels
    const fuels = Array.from(new Set(filteredVehicles.map(v => v.fuel).filter(Boolean)));
    fuels.forEach(fuel => {
      if (fuel.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.fuel === fuel).length;
        results.push({ type: 'fuel', text: fuel, filterKey: 'fuel', filterValue: fuel, count });
      }
    });

    // Suggest transmissions
    const transmissions = Array.from(new Set(filteredVehicles.map(v => v.transmission).filter(Boolean)));
    transmissions.forEach(transmission => {
      if (transmission.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.transmission === transmission).length;
        results.push({ type: 'transmission', text: transmission, filterKey: 'transmission', filterValue: transmission, count });
      }
    });

    // Suggest colors
    const colors = Array.from(new Set(filteredVehicles.map(v => v.color).filter(Boolean)));
    colors.forEach(color => {
      if (color.toLowerCase().includes(term)) {
        const count = filteredVehicles.filter(v => v.color === color).length;
        results.push({ type: 'color', text: color, filterKey: 'color', filterValue: color, count });
      }
    });

    // Suggest specific vehicles (no count needed)
    filteredVehicles.forEach(vehicle => {
      const fullText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`.toLowerCase();
      if (fullText.includes(term) && results.length < 15) {
        const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version}`;
        if (!results.find(s => s.text === vehicleText)) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      }
    });

    // NEW: Suggest vehicles that match multiple criteria from search term
    // Examples: "ford 2025", "ford territory 2025 branco", "ford preto", "territory branco", "preto 2025", "ford gasolina"
    const searchWords = term.split(/\s+/).filter(word => word.length > 0);
    if (searchWords.length > 1) {
      const matchingVehicles = filteredVehicles.filter(vehicle => {
        // Check if vehicle matches ALL search words
        return searchWords.every(word => {
          const vehicleText = `${vehicle.brand} ${vehicle.model} ${vehicle.version} ${vehicle.modelYear} ${vehicle.color} ${vehicle.fuel} ${vehicle.transmission}`.toLowerCase();
          return vehicleText.includes(word);
        });
      });

      // Add matching vehicles to results
      matchingVehicles.forEach(vehicle => {
        const vehicleText = `${vehicle.brand} ${vehicle.model}${vehicle.version ? ` ${vehicle.version}` : ''}`;
        if (!results.find(s => s.text === vehicleText) && results.length < 15) {
          results.push({
            type: 'vehicle',
            text: vehicleText,
            vehicle: vehicle
          });
        }
      });
    }

    return results.slice(0, 10); // Limit to 10 suggestions
  }, [searchTerm, filteredVehicles, brands, allVehicles, config?.stores]);

  // Reset banner index when store changes
  useEffect(() => {
    setCurrentBannerIndex(0);
    setNextBannerIndex(0);
    setIsTransitioning(false);
  }, [store._id]);

  // Auto-rotate banners every 5 seconds when there are multiple banners
  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        const nextIndex = (currentBannerIndex + 1) % banners.length;
        setNextBannerIndex(nextIndex);
        setIsTransitioning(true);

        // Após o fade in da próxima imagem (300ms), fazer o fade out da anterior
        setTimeout(() => {
          setCurrentBannerIndex(nextIndex);
          setIsTransitioning(false);
        }, 10); // Duração completa da transição
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [banners.length, currentBannerIndex]);

  const handleBannerChange = (index: number) => {
    if (index !== currentBannerIndex && !isTransitioning) {
      setNextBannerIndex(index);
      setIsTransitioning(true);

      setTimeout(() => {
        setCurrentBannerIndex(index);
        setIsTransitioning(false);
      }, 10);
    }
  };

  const currentBanner = banners.length > 0 ? banners[currentBannerIndex] : null;
  const nextBanner = banners.length > 0 ? banners[nextBannerIndex] : null;
  const currentBannerImage = currentBanner ? currentBanner.image : '';
  const nextBannerImage = nextBanner ? nextBanner.image : '';

  // Preload next banner images for smooth transitions
  useEffect(() => {
    if (banners.length > 1) {
      // Preload next banner (for smooth transition)
      const nextBanner = banners[nextBannerIndex]?.image;
      if (nextBanner) {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'image';
        link.href = nextBanner;
        document.head.appendChild(link);
      }
    }
  }, [banners, nextBannerIndex]);

  const handleSearch = () => {
    if (searchTerm.trim()) {
      navigate(`/estoque?search=${encodeURIComponent(searchTerm.trim())}`);
    } else {
      navigate('/estoque');
    }
  };

  const handleBannerClick = async (banner: any) => {
    if (!banner || !banner.type) return;

    switch (banner.type) {
      case 'ad':
        // Check number of values
        if (banner.values && banner.values.length > 0) {
          if (banner.values.length === 1) {
            // Single vehicle - redirect to vehicle detail with slug
            const vehicleId = banner.values[0].value;
            const vehicle = vehicles.find(v => v.id.toString() === vehicleId.toString());
            if (vehicle) {
              const slug = await generateVehicleSlug(vehicle);
              navigate(`/estoque/${slug}`);
            } else {
              navigate(`/estoque/${vehicleId}`); // Fallback to ID
            }
          } else {
            // Multiple vehicles - redirect to banner vehicles page
            const vehicleIds = banner.values.map((v: any) => v.value).join(',');
            const title = encodeURIComponent(banner.title || 'Veículos em Promoção');
            navigate(`/destaques?ids=${vehicleIds}&title=${title}`);
          }
        }
        break;

      case 'link':
        // Open external link
        if (banner.values && banner.values.length > 0 && banner.values[0].value) {
          window.open(banner.values[0].value, '_blank');
        }
        break;

      case null:
      case 'null':
        // No action
        break;

      default:
        break;
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        handleSearch();
      }
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev =>
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedSuggestionIndex >= 0) {
        handleSuggestionClick(suggestions[selectedSuggestionIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = async (suggestion: any) => {
    setShowSuggestions(false);

    if (suggestion.type === 'vehicle' && suggestion.vehicle) {
      // Navigate to vehicle detail with slug (await async function)
      const slug = await generateVehicleSlug(suggestion.vehicle);
      navigate(`/estoque/${slug}`);
    } else if (suggestion.type === 'store' && suggestion.storeId) {
      // Navigate to vehicle listing filtered by store
      navigate(`/estoque?store=${suggestion.storeId}`);
    } else if (suggestion.type === 'year' && suggestion.minYear && suggestion.maxYear) {
      // Navigate with year range
      navigate(`/estoque?minYear=${suggestion.minYear}&maxYear=${suggestion.maxYear}`);
    } else if (suggestion.filterKey && suggestion.filterValue) {
      // Navigate with specific filter
      navigate(`/estoque?${suggestion.filterKey}=${encodeURIComponent(suggestion.filterValue)}`);
    } else {
      // Fill search term and navigate to vehicle listing
      setSearchTerm(suggestion.text);
      navigate(`/estoque?search=${encodeURIComponent(suggestion.text)}`);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (value.length >= 2) {
      updateDropdownPosition();
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
    setSelectedSuggestionIndex(-1);
  };

  const handleSearchFocus = () => {
    // Scroll to position the search card nicely below the header
    // Use requestAnimationFrame to avoid forced reflow
    requestAnimationFrame(() => {
      if (searchCardRef.current && !isMobile) {
        const headerHeight = 155; // Header height
        const offset = 16; // pt-4 = 16px
        const targetPosition = searchCardRef.current.getBoundingClientRect().top + window.scrollY - headerHeight - offset;

        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });
      }
    });
  };

  const updateDropdownPosition = () => {
    const activeInput = isFloatingSearchVisible && floatingInputRef.current ? floatingInputRef.current : inputRef.current;

    if (activeInput) {
      const rect = activeInput.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  // Check if brands container has overflow
  useEffect(() => {
    const checkBrandsOverflow = () => {
      // Use requestAnimationFrame to avoid forced reflow
      requestAnimationFrame(() => {
        if (brandsScrollRef.current) {
          const hasOverflow = brandsScrollRef.current.scrollWidth > brandsScrollRef.current.clientWidth;
          setShowBrandNavigation(hasOverflow);
        }
      });
    };

    // Delay initial check to ensure DOM is ready
    const timeoutId = setTimeout(checkBrandsOverflow, 0);
    
    // Throttle resize events to avoid excessive checks
    let resizeTimeout: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(checkBrandsOverflow, 100);
    };
    
    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, [brands]);

  // Detect when search card is out of view
  useEffect(() => {
    if (isMobile) return; // Only for desktop

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingSearch(!entry.isIntersecting);
      },
      {
        threshold: 0,
        rootMargin: '-80px 0px 0px 0px', // Offset for header height
      }
    );

    const currentRef = searchCardRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [isMobile]);

  // Close suggestions when clicking outside and update position on scroll/resize
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        searchRef.current &&
        !searchRef.current.contains(target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(target)
      ) {
        setShowSuggestions(false);
      }
    };

    const handleScroll = (e: Event) => {
      // Não fazer nada se o scroll for dentro do próprio dropdown
      if (dropdownRef.current && dropdownRef.current.contains(e.target as Node)) {
        return;
      }
      // Atualizar a posição do dropdown ao invés de fechá-lo
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    const handleResize = () => {
      if (showSuggestions) {
        updateDropdownPosition();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', handleResize);
    };
  }, [showSuggestions, isFloatingSearchVisible]);

  // Handle floating search animation (show/hide with exit animation)
  useEffect(() => {
    if (showFloatingSearch) {
      // Show immediately
      setIsFloatingSearchVisible(true);
      setIsExiting(false);
    } else if (isFloatingSearchVisible && !showFloatingSearch) {
      // Start exit animation
      setIsExiting(true);
      // Remove from DOM after animation completes
      const timeout = setTimeout(() => {
        setIsFloatingSearchVisible(false);
        setIsExiting(false);
      }, 300);
      return () => clearTimeout(timeout);
    }
  }, [showFloatingSearch]);

  // Close suggestions when switching between inputs to avoid weird transition
  useEffect(() => {
    setShowSuggestions(false);
  }, [isFloatingSearchVisible]);

  const desktopSearchCard = (
    <>
      <h1 className="sr-only">
        {store.company} - Veículos Usados e Seminovos
      </h1>
      <div
        ref={searchCardRef}
        className="rounded-2xl p-2 pointer-events-auto bg-white"
        onClick={(e) => e.stopPropagation()}
        style={{
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
        }}
      >
        <div className="bg-white rounded-xl p-1">
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 relative" ref={searchRef}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder={animatedPlaceholder || 'Pesquisar por ...'}
                className="h-12 pl-12 text-base border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none bg-transparent"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  handleSearchFocus();
                  if (searchTerm.length >= 2) {
                    updateDropdownPosition();
                    setShowSuggestions(true);
                  }
                }}
              />
            </div>
            <Button
              size="lg"
              className="h-12 px-8 text-white font-bold text-base transition-all"
              style={{
                background: `linear-gradient(135deg, ${buttonColor}, ${buttonColor}ee)`,
              }}
              onClick={handleSearch}
            >
              <span className="hidden md:inline">Pesquisar</span>
              <span className="md:hidden">BUSCAR</span>
            </Button>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <section
      id="inicio"
      className={`relative mb-0 bg-muted/30 hero-layout-1${!isMobile && banners.length > 0 ? ' hero-layout-1--banner-card' : ''}`}
      style={{ marginTop: isMobile ? '114px' : '124px' }}
    >
      {/* Mobile Search Bar - Above banner on mobile only */}
      {isMobile && (
        <div className="px-4 pt-5 pb-4">
          {/* H1 Tag for SEO - Hidden visually but accessible to screen readers */}
          <h1 className="sr-only">
            {store.company} - Sua próxima aventura sobre rodas está a apenas um toque de distância!
          </h1>
          
          <div className="bg-white rounded-xl p-2.5">
            <div className="relative" ref={searchRef}>
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                ref={inputRef}
                placeholder={animatedPlaceholder || 'Pesquisar por ...'}
                className="h-10 pl-10 pr-3 text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none bg-transparent"
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={handleKeyPress}
                onFocus={() => {
                  if (searchTerm.length >= 2) {
                    updateDropdownPosition();
                    setShowSuggestions(true);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Mobile Banner Container - Show only if has mobile banners */}
      {isMobile && banners.length > 0 && (
        <div className="px-4 pb-6">
          <div
            className="relative overflow-hidden rounded-2xl border-2 border-gray-200"
            style={{
              aspectRatio: '480 / 280',
              backgroundColor: banners.length === 0 ? primaryColor : undefined,
            }}
          >
            <div className="absolute inset-0">
              {currentBannerImage && (
                <img
                  src={currentBannerImage}
                  alt=""
                  className="hidden"
                  loading="eager"
                  fetchPriority="high"
                  onLoad={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div
                className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                style={{
                  backgroundImage: `url(${currentBannerImage})`,
                  backgroundSize: 'contain',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  cursor: currentBanner?.type && currentBanner.type !== 'none' && currentBanner.type !== null ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (currentBanner?.type && currentBanner.type !== 'null' && currentBanner.type !== null) {
                    handleBannerClick(currentBanner);
                  }
                }}
              />
              {isTransitioning && (
                <div
                  className="absolute inset-0 transition-opacity duration-700 ease-in-out"
                  style={{
                    backgroundImage: `url(${nextBannerImage})`,
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    opacity: isTransitioning ? 1 : 0,
                  }}
                />
              )}
              {banners.length > 1 && (
                <>
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white rounded-full p-2 transition-all duration-300 hover:scale-110 pointer-events-auto border border-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      const prevIndex = currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1;
                      handleBannerChange(prevIndex);
                    }}
                    aria-label="Banner anterior"
                  >
                    <ChevronLeft className="h-4 w-4 text-gray-800" />
                  </button>
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white rounded-full p-2 transition-all duration-300 hover:scale-110 pointer-events-auto border border-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextIndex = (currentBannerIndex + 1) % banners.length;
                      handleBannerChange(nextIndex);
                    }}
                    aria-label="Próximo banner"
                  >
                    <ChevronRight className="h-4 w-4 text-gray-800" />
                  </button>
                </>
              )}
              {banners.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex gap-1 pointer-events-auto">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      className={`h-1.5 rounded-full transition-all duration-300 ${index === currentBannerIndex
                        ? 'w-6 bg-white'
                        : 'w-1.5 bg-white/50 hover:bg-white/75'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBannerChange(index);
                      }}
                      aria-label={`Ir para banner ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Banner Container - Desktop: always show */}
      {!isMobile && (
        <div
          className={`relative overflow-hidden mb-8 md:mb-0 mx-4 md:mx-0 rounded-2xl md:rounded-none border-2 border-gray-200 md:border-0${banners.length > 0 ? ' full-banner full-banner--desktop full-banner--card full-banner--layout1' : ''}`}
          style={{
            height: banners.length === 0 ? '120px' : undefined,
            backgroundColor: banners.length === 0 ? primaryColor : undefined,
          }}
        >
          {/* Banner Background */}
          {banners.length > 0 ? (
            <>
            <div className="full-banner__blur" aria-hidden="true">
              <div
                className="full-banner__blur-bg transition-opacity duration-700 ease-in-out"
                style={{ backgroundImage: `url(${currentBannerImage})` }}
              />
              {isTransitioning && (
                <div
                  className="full-banner__blur-bg transition-opacity duration-700 ease-in-out"
                  style={{
                    backgroundImage: `url(${nextBannerImage})`,
                    opacity: isTransitioning ? 1 : 0,
                  }}
                />
              )}
              <div className="full-banner__blur-overlay" />
            </div>
            <div className="full-banner__frame">
            <div className="absolute inset-0">
              {/* Current Banner */}
              {/* Preload image with img tag for better LCP */}
              {currentBannerImage && (
                <img
                  src={currentBannerImage}
                  alt=""
                  className="hidden"
                  loading="eager"
                  fetchpriority="high"
                  onLoad={(e) => {
                    // Image loaded, now show background
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              )}
              <div
                className="full-banner__bg transition-opacity duration-700 ease-in-out"
                style={{
                  backgroundImage: `url(${currentBannerImage})`,
                  cursor: currentBanner?.type && currentBanner.type !== 'none' && currentBanner.type !== null ? 'pointer' : 'default'
                }}
                onClick={() => {
                  if (currentBanner?.type && currentBanner.type !== 'null' && currentBanner.type !== null) {
                    handleBannerClick(currentBanner);
                  }
                }}
              />

              {/* Next Banner with fade effect */}
              {isTransitioning && (
                <div
                  className="full-banner__bg transition-opacity duration-700 ease-in-out"
                  style={{
                    backgroundImage: `url(${nextBannerImage})`,
                    opacity: isTransitioning ? 1 : 0
                  }}
                />
              )}

              {/* Dark Overlay for better text visibility - não bloqueia cliques. Hidden on mobile since search is below */}
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-transparent md:to-black/70 pointer-events-none" />

              {/* Navigation Arrows */}
              {banners.length > 1 && (
                <>
                  <button
                    className="absolute left-4 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white rounded-full p-3 transition-all duration-300 hover:scale-110 pointer-events-auto border border-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      const prevIndex = currentBannerIndex === 0 ? banners.length - 1 : currentBannerIndex - 1;
                      handleBannerChange(prevIndex);
                    }}
                    aria-label="Banner anterior"
                  >
                    <ChevronLeft className="h-6 w-6 text-gray-800" />
                  </button>

                  <button
                    className="absolute right-4 top-1/2 -translate-y-1/2 z-20 bg-white/95 hover:bg-white rounded-full p-3 transition-all duration-300 hover:scale-110 pointer-events-auto border border-gray-200"
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextIndex = (currentBannerIndex + 1) % banners.length;
                      handleBannerChange(nextIndex);
                    }}
                    aria-label="Próximo banner"
                  >
                    <ChevronRight className="h-6 w-6 text-gray-800" />
                  </button>
                </>
              )}

              {/* Banner Indicators */}
              {banners.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20 flex gap-2 pointer-events-auto">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      className={`h-2 rounded-full transition-all duration-300 ${index === currentBannerIndex
                        ? 'w-8 bg-white'
                        : 'w-2 bg-white/50 hover:bg-white/75'
                        }`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleBannerChange(index);
                      }}
                      aria-label={`Ir para banner ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
            </div>

            <div className="hero-search-slot w-full">
              <div className="container mx-auto px-4">
                <div className="max-w-4xl mx-auto">
                  {desktopSearchCard}
                </div>
              </div>
            </div>
            </>
          ) : (
            // Fallback solid background with primary color if no banners
            <div className="absolute inset-0" style={{ backgroundColor: primaryColor }} />
          )}
        </div>
      )}

      {/* Search + brands — Desktop */}
      {!isMobile && (
        <div className={`relative z-30 mb-0 pointer-events-none${banners.length > 0 ? '' : ' -mt-14'}`}>
          <div className="container mx-auto px-4">
            {banners.length === 0 && (
              <div className="max-w-4xl mx-auto">
                {desktopSearchCard}
              </div>
            )}

            {/* Available Brands - Below search card, same max width */}
            {brands.length > 0 && (
              <div className={`container mx-auto px-4 pointer-events-auto${banners.length > 0 ? ' hero-brands-slot' : ' mt-6'}`}>
                <div className="max-w-4xl mx-auto px-12">
                  <div className="relative group">
                    {/* Scroll Container with Gradient Masks */}
                    <div className="relative">
                      <div
                        ref={brandsScrollRef}
                        id="brands-scroll-desktop"
                        className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth pb-2 px-4"
                        style={{
                          scrollbarWidth: 'none',
                          msOverflowStyle: 'none',
                          justifyContent: showBrandNavigation ? 'flex-start' : 'center',
                        }}
                      >

                        {brandsLoading ? (
                          /* Loading Skeleton */
                          <div className="flex gap-3 justify-center">
                            {Array.from({ length: brands.length }).map((_, index) => (
                              <div
                                key={index}
                                className="flex-shrink-0 w-16 h-16 border-2 border-gray-200 rounded-xl bg-gray-100 animate-pulse"
                              />
                            ))}
                          </div>
                        ) : brands.length > 0 && brands.map((brand) => {
                          const vehicleType = getBrandVehicleType(brand, filteredVehicles);
                          const logo = getBrandLogo(brand, vehicleType);
                          return (
                            <button
                              key={brand}
                              className="flex-shrink-0 w-16 h-16 border-2 border-gray-200 rounded-xl bg-white hover:border-gray-400 transition-all duration-200 p-2 flex flex-col items-center justify-center gap-1 group/brand"
                              onClick={() => navigate(`/estoque?search=${encodeURIComponent(brand)}`)}
                            >
                              {logo ? (
                                <div className="w-full h-full flex items-center justify-center">
                                  <img
                                    src={logo}
                                    alt={brand}
                                    className="max-w-full max-h-full object-contain group-hover/brand:scale-110 transition-transform"
                                    width={44}
                                    height={44}
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      if (e.currentTarget.nextElementSibling) {
                                        e.currentTarget.nextElementSibling.classList.remove('hidden');
                                      }
                                    }}
                                  />
                                  <span className="hidden text-[10px] font-semibold text-center">{brand}</span>
                                </div>
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <span className="text-[10px] font-semibold text-center">{brand}</span>
                                </div>
                              )}
                            </button>
                          );
                        })
                        }
                      </div>

                      {/* Shadow Fade Masks - Only show if there's overflow */}
                      {showBrandNavigation && (
                        <div
                          className="absolute inset-0 pointer-events-none"
                          style={{
                            boxShadow: 'inset 40px 0 30px -20px rgba(237, 240, 244, 1), inset -40px 0 30px -20px rgba(237, 240, 244, 1)',
                          }}
                        />
                      )}
                    </div>

                    {/* Navigation Arrows - Only show if there's overflow */}
                    {showBrandNavigation && (
                      <>
                        <button
                          className="absolute -left-12 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover:opacity-100 z-20 border border-gray-200 shadow-lg"
                          onClick={() => {
                            const container = document.getElementById('brands-scroll-desktop');
                            if (container) container.scrollLeft -= 200;
                          }}
                          aria-label="Scroll para esquerda"
                        >
                          <ChevronLeft className="h-5 w-5 text-gray-800" />
                        </button>

                        <button
                          className="absolute -right-12 top-1/2 -translate-y-1/2 bg-white/95 hover:bg-white rounded-full p-2.5 transition-all opacity-0 group-hover:opacity-100 z-20 border border-gray-200 shadow-lg"
                          onClick={() => {
                            const container = document.getElementById('brands-scroll-desktop');
                            if (container) container.scrollLeft += 200;
                          }}
                          aria-label="Scroll para direita"
                        >
                          <ChevronRight className="h-5 w-5 text-gray-800" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Search Bar - Shows when main search is out of view */}
      {!isMobile && isFloatingSearchVisible && createPortal(
        <div
          className={`fixed top-[112px] left-0 right-0 z-40 transition-all duration-300 ${isExiting ? 'animate-out slide-out-to-top-2 fade-out' : 'animate-in slide-in-from-top-2 fade-in'
            }`}
        >
          <div className="container mx-auto px-4 py-6">
            <div className="max-w-[500px] mx-auto">
              <div className="rounded-lg p-1.5 bg-white shadow-lg">
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      ref={floatingInputRef}
                      placeholder={animatedPlaceholder || 'Pesquisar por ...'}
                      className="h-10 pl-10 text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none bg-transparent"
                      value={searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      onKeyDown={handleKeyPress}
                      onFocus={() => {
                        if (searchTerm.length >= 2) {
                          updateDropdownPosition();
                          setShowSuggestions(true);
                        }
                      }}
                    />
                  </div>
                  <Button
                    size="sm"
                    className="h-10 px-6 text-white font-semibold transition-all"
                    style={{
                      background: `linear-gradient(135deg, ${buttonColor}, ${buttonColor}ee)`,
                    }}
                    onClick={handleSearch}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Autocomplete Suggestions Portal */}
      {showSuggestions && suggestions.length > 0 && (isFloatingSearchVisible || dropdownPosition.width > 0) && createPortal(
        <div
          ref={dropdownRef}
          className={`${isFloatingSearchVisible ? 'fixed' : 'absolute'} bg-card/95 backdrop-blur-md border border-border rounded-xl max-h-[400px] overflow-y-auto`}
          style={{
            top: isFloatingSearchVisible ? '194px' : `${dropdownPosition.top}px`,
            left: isFloatingSearchVisible ? '50%' : `${dropdownPosition.left}px`,
            transform: isFloatingSearchVisible ? 'translateX(-50%)' : 'none',
            width: isFloatingSearchVisible ? '500px' : `${dropdownPosition.width}px`,
            maxWidth: 'calc(100vw - 2rem)',
            zIndex: 40,
            scrollbarWidth: 'thin',
            scrollbarColor: 'hsl(var(--muted-foreground) / 0.3) transparent',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => {
                handleSuggestionClick(suggestion);
              }}
              className={`group w-full text-left px-4 py-3 transition-all duration-200 border-b border-border/30 last:border-b-0 first:rounded-t-xl last:rounded-b-xl ${index === selectedSuggestionIndex
                ? 'bg-accent/10 border-l-2 border-l-accent pl-[14px]'
                : 'hover:bg-muted/50 pl-[14px'
                }`}
            >
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                  {suggestion.type === 'store' && (
                    <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center">
                      <Building2 className="h-4 w-4 text-purple-500" />
                    </div>
                  )}
                  {suggestion.type === 'category' && (
                    <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
                      {suggestion.text === 'Carro' && <Car className="h-4 w-4 text-green-500" />}
                      {suggestion.text === 'Moto' && <Bike className="h-4 w-4 text-green-500" />}
                      {suggestion.text === 'Caminhão' && <Truck className="h-4 w-4 text-green-500" />}
                    </div>
                  )}
                  {suggestion.type === 'brand' && (
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <Search className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  {suggestion.type === 'model' && (
                    <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center">
                      <Car className="h-4 w-4 text-blue-500" />
                    </div>
                  )}
                  {suggestion.type === 'year' && (
                    <div className="w-8 h-8 rounded-full bg-orange-500/10 flex items-center justify-center">
                      <Calendar className="h-4 w-4 text-orange-500" />
                    </div>
                  )}
                  {suggestion.type === 'fuel' && (
                    <div className="w-8 h-8 rounded-full bg-yellow-500/10 flex items-center justify-center">
                      <Fuel className="h-4 w-4 text-yellow-500" />
                    </div>
                  )}
                  {suggestion.type === 'transmission' && (
                    <div className="w-8 h-8 rounded-full bg-indigo-500/10 flex items-center justify-center">
                      <Settings className="h-4 w-4 text-indigo-500" />
                    </div>
                  )}
                  {suggestion.type === 'color' && (
                    <div className="w-8 h-8 rounded-full bg-pink-500/10 flex items-center justify-center">
                      <Palette className="h-4 w-4 text-pink-500" />
                    </div>
                  )}
                  {suggestion.type === 'vehicle' && suggestion.vehicle && (
                    <img
                      src={suggestion.vehicle.photos[0]}
                      alt={suggestion.text}
                      className="w-12 h-12 object-cover rounded"
                    />
                  )}
                </div>
                <div className="flex-1 min-w-0 flex items-center justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-foreground truncate">
                      {suggestion.text}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {suggestion.type === 'store' && <span>Loja</span>}
                      {suggestion.type === 'category' && <span>Categoria</span>}
                      {suggestion.type === 'brand' && <span>Marca</span>}
                      {suggestion.type === 'model' && <span>Modelo</span>}
                      {suggestion.type === 'year' && <span>Ano</span>}
                      {suggestion.type === 'fuel' && <span>Combustível</span>}
                      {suggestion.type === 'transmission' && <span>Câmbio</span>}
                      {suggestion.type === 'color' && <span>Cor</span>}
                      {suggestion.type === 'vehicle' && suggestion.vehicle && (
                        `${suggestion.vehicle.modelYear} • ${new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(suggestion.vehicle.price)}`
                      )}
                    </div>
                  </div>
                  {suggestion.count && (
                    <div className="flex-shrink-0">
                      <span className="flex items-center justify-center px-2 h-6 text-[10px] bg-muted rounded-full font-semibold text-muted-foreground">
                        {suggestion.count}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>,
        document.body
      )}
    </section>
  );
};

export default Hero;
